import { getAmplitudeClient } from '@/features/amplitude';
import { usePostHog } from 'posthog-js/react';

const actionsDictionary = {
  interview: {
    SAVED: 'saved',
    CREATED: 'created',
    ADD_QUESTIONS_CLICKED: 'add_questions_clicked',
    QUESTIONS_SAVED: 'questions_saved',
  },
  interview_evaluation: {
    SAVED: 'saved',
    DECISION_CLICKED: 'decision_clicked',
    RATING_CLICKED: 'rating_clicked',
  },
  test_analytics: {
    PAGE_VIEWED: 'page_viewed',
    TEST_NAME_CHANGED: 'test_name_changed',
    TIME_PERIOD_CHANGED: 'time_period_changed',
    CHART_TYPE_CHANGED: 'chart_type_changed',
  },
  assessment_builder: {
    PAGE_VIEWED: 'page_viewed',
    ASSESSMENT_TYPE_SELECTED: 'assessment_type_selected',
    CREATION_METHOD_SELECTED: 'creation_method_selected',
  },
  upsell: {
    UPGRADE_CLICKED: 'upgrade_clicked',
    VIEWED: 'viewed',
    TOP_BANNER_UPGRADED: 'top_banner_upgraded',
  },
  pipeline: {
    AUTO_GENERATED_JOB_NAME_CHANGED: 'auto_generated_job_name_changed',
  },
} as const;

type Object = keyof typeof actionsDictionary;
type ActionsByObject<T extends Object> =
  (typeof actionsDictionary)[T][keyof (typeof actionsDictionary)[T]];
type Data = { [k: string]: string | number | boolean | null };

/**
 * Hook for tracking analytics events in both Amplitude and PostHog
 * @param object - The analytics object ('interview' or 'interview_evaluation' for now), for more information see https://www.notion.so/toggl/Posthog-Events-Actions-and-Properties-131fe85cbb6380a388e5d4d7707fb0fd#131fe85cbb6380e5b245cc85a65b62d2
 * @param sharedData - Optional shared data to include with all events
 * @returns An object containing actions dictionary and trackEvent function
 * @returns {object} actions - Dictionary of available actions for the given object type
 * @returns {function} trackEvent - Function to track an analytics event with optional data
 */
export function useAnalytics<T extends Object>(object: T, sharedData?: Data) {
  const amplitude = getAmplitudeClient();
  const posthog = usePostHog();

  function trackEvent(action: ActionsByObject<T>, data?: Data) {
    amplitude?.logEvent(`${object}/${action}`, { ...sharedData, ...data });
    posthog?.capture(`hire_admin_app_${object}:${action}`, {
      ...sharedData,
      ...data,
    });
  }

  return {
    actions: actionsDictionary[object],
    trackEvent,
  };
}
